import {ScreenNames} from '../../../lib/analytics/events';
import {BaseScreen, ScreenDivider, ScreenHeader} from '../Screen';
import {UserLoader} from '../../../components/UserLoader';
import {toast, ToastContainer} from 'react-toastify';
import React, {useMemo} from 'react';
import {ReactComponent as UsersIcon} from 'src/assets/icons/users-01.svg';
import {UserProfileInterface} from '../../../reducers/users';
import {DataLoader} from '../../../components/DataLoader';

import UserMgmtCrud from './api';
import {UserAccess, UserRole} from './types';
import {Input} from '../profile/Profile';
import {DateTime} from 'luxon';

const styles = {
    table: 'table-fixed w-full text-center',
    tr: 'border-b border-gray-200',
    th: 'p-2',
    td: 'p-2 text-center',
    tdLeft: 'p-2 text-left',
};

function UserInviteForm(props: {org_uid: string; onAdd: (user: UserAccess) => void}) {
    const [email, setEmail] = React.useState<string>('');
    const [name, setName] = React.useState<string>('');
    const [role, setRole] = React.useState<UserRole>('Member');

    const formReady = useMemo(() => {
        return email.length > 0 && name.length > 0;
    }, [email, name]);

    const clearForm = () => {
        setEmail('');
        setName('');
        setRole('Member');
    };

    const processInvite = () => {
        UserMgmtCrud.invite_user(props.org_uid, {
            email,
            first_name: name,
            last_name: '',
            role,
        })
            .then((result) => {
                if (!!result.error) throw result.error;
                props.onAdd(result.data!);
                clearForm();
                toast('Invited user', {type: 'success'});
            })
            .catch((err) => {
                toast('Failed to invite user. Error: ' + (err.message ?? 'unknown error'), {
                    type: 'error',
                });
            });
    };

    return (
        <div className={'flex flex-row gap-2 p-2'}>
            <div className={'grow'}>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={'Email'} />
            </div>
            <div className={'grow'}>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={'Name'} />
            </div>
            <div className={''}>
                <select
                    className={'w-full rounded-md border border-solid border-gray-200 p-2'}
                    value={role}
                    onChange={(e) => setRole(e.target.value as UserRole)}>
                    <option value={'Admin'}>Admin</option>
                    <option value={'Member'}>Member</option>
                </select>
            </div>
            <div className={''}>
                <input
                    className={
                        !formReady
                            ? 'rounded-md bg-gray-200 p-2 text-gray-500'
                            : 'rounded-md bg-green-500 p-2 text-white'
                    }
                    value={'Invite'}
                    type={'submit'}
                    disabled={!formReady}
                    onClick={() => processInvite()}
                />
            </div>
        </div>
    );
}

function NopForm(props: {children: React.ReactNode}) {
    return <form onSubmit={(e) => e.preventDefault()}>{props.children}</form>;
}

function RemoveButton(props: {access: UserAccess; onRemove: () => void}) {
    const [loading, setLoading] = React.useState<boolean>(false);
    const {access, onRemove} = props;
    if (access.role === 'Owner') return null;
    const processRemove = () => {
        setLoading(true);
        UserMgmtCrud.remove_user(access.organization_uid, access)
            .then((result) => {
                if (!!result.error) throw result.error;
                onRemove();
                if (access.state === 'Pending') {
                    // this is terrible
                    toast('Revoked Invitation', {type: 'success'});
                } else {
                    toast('Removed User', {type: 'success'});
                }
            })
            .catch((err) => {
                toast('Failed to remove user. Error: ' + (err.message ?? 'unknown error'), {
                    type: 'error',
                });
            });
    };

    return (
        <button disabled={loading} className={'rounded-md bg-red-500 p-2 text-white'} onClick={() => processRemove()}>
            Remove
        </button>
    );
}

function UserAccessMgmt(props: {org_uid: string; access: UserAccess[]}) {
    let [access, setAccess] = React.useState<UserAccess[]>(props.access);

    function RenderName(props: {user: UserAccess}) {
        const {user} = props;
        if (user.first_name) {
            let name = user.first_name;
            if (!!user.last_name) name += ' ' + user.last_name;
            return (
                <>
                    <span className={'text-gray-900'}>{name}</span>
                    <br />
                    <span className={'text-small text-gray-500'}>{user.email}</span>
                </>
            );
        }
        return (
            <>
                <span className={'text-gray-900'}>{user.email}</span>
            </>
        );
    }

    return (
        <>
            <table className={styles.table}>
                <thead>
                    <tr className={styles.tr}>
                        <th className={styles.th}>User</th>
                        <th className={styles.th}>Role</th>
                        <th className={styles.th}>State</th>
                        <th className={styles.th}>Time</th>
                        <th className={styles.th}>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {access.map((a, i) => {
                        return (
                            <tr className={styles.tr} key={i}>
                                <td className={`${styles.tdLeft}`}>
                                    <RenderName user={a} />
                                </td>
                                <td className={styles.td}>{a.role}</td>
                                <td className={styles.td}>{a.state}</td>
                                <td className={styles.td}>
                                    {DateTime.fromISO(a.updated_at).toLocaleString(DateTime.DATETIME_MED)}
                                </td>
                                <td className={styles.td}>
                                    <RemoveButton
                                        access={a}
                                        onRemove={() => {
                                            let newAccess = access.filter((u) =>
                                                u.username ? u.username !== a.username : u.email !== a.email,
                                            );
                                            setAccess(newAccess);
                                        }}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                    <tr className={styles.tr}>
                        <td className={styles.td} colSpan={5}>
                            <span className={'italic text-gray-900'}>Invite a new user below</span>
                        </td>
                    </tr>
                </tbody>
            </table>
            <NopForm>
                <UserInviteForm org_uid={props.org_uid} onAdd={(user) => setAccess([...access, user])} />
            </NopForm>
        </>
    );
}

function BaseFetch(props: {user: UserProfileInterface}) {
    const {user} = props;
    const org = user.currentOrganization;

    return (
        <DataLoader
            loader={async () => {
                const result = await UserMgmtCrud.list(org.uid);
                if (!!result.error) throw result.error;
                return result.data ?? [];
            }}>
            {(data) => <UserAccessMgmt org_uid={org.uid} access={data} />}
        </DataLoader>
    );
}

export function UserManagementScreen() {
    const {USER_MANAGEMENT} = ScreenNames;

    return (
        <BaseScreen screenName={USER_MANAGEMENT}>
            <ScreenHeader icon={<UsersIcon className={'stroke-black'} />} title={'Users'} />
            <ScreenDivider />
            <UserLoader>{(user) => <BaseFetch user={user} />}</UserLoader>
            <ToastContainer />
        </BaseScreen>
    );
}
